<template>
  <div class="squircle-xl bg-primary-50 flex flex-col gap-2 rounded border p-2">
    <h4
      class="capitalize-first flex justify-between text-lg leading-tight font-bold"
    >
      <span>{{ $t('global.new_supplier_order') }}</span>
      <BaseButton
        :title="$t('button.back')"
        color="warning"
        size="extra-small"
        @click="() => $emit('change-view')"
      />
    </h4>
    <div class="squircle-xl flex flex-col gap-2 border bg-white p-4">
      <div
        v-if="newOrders?.length > 0"
        class="mt-2 grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
      >
        <StockSelectSupplierOrder
          v-for="supplier_order in newOrders"
          :key="supplier_order.id"
          :supplier-order="supplier_order"
          :disabled="supplier_order.recipient_id !== localSupplierId"
          :selected="
            !newSupplierOrder &&
            localOriginal.supplier_order_id === supplier_order.id
          "
          @click="() => handleSupplierOrderClick(supplier_order)"
        />
      </div>
      <div>
        <h5 class="capitalize-first text-base font-bold">
          {{ $t('global.delivery') }}
        </h5>
        <div
          class="grid auto-rows-fr grid-cols-[repeat(auto-fill,_minmax(14rem,_1fr))] gap-4"
        >
          <button
            v-for="event in events"
            :key="event.id"
            class="squircle-lg flex flex-col rounded-md border p-2 text-left disabled:bg-gray-100 disabled:text-gray-700"
            :class="{
              'border-primary bg-primary text-contrast-primary':
                event.id === localDeliveryToEventId,
              'border-slate-200': event.id !== localDeliveryToEventId
            }"
            :disabled="isDisabled(event.date_from)"
            @click="localDeliveryToEventId = event.id"
          >
            <span class="font-bold">{{ event.default_title }}</span>
            <span class="text-xs">{{ event.place_model?.name }}</span>
            <span
              v-if="event.type === 'VIS' && event.properties.visit_type"
              class="text-xs"
            >
              {{ $t(`enums.event.visiteTypes.${event.properties.visit_type}`) }}
            </span>
            <span class="text-xs">
              {{ $h.formatDate(event.date_from, 'LLLL') }}
            </span>
          </button>
          <button
            v-if="events?.length > 0"
            class="squircle-lg rounded-md p-2 font-bold uppercase"
            :class="{
              'border-primary bg-primary text-contrast-primary':
                null === localDeliveryToEventId,
              'border-2 border-dashed border-slate-200':
                null !== localDeliveryToEventId
            }"
            @click="localDeliveryToEventId = null"
          >
            {{ $t('global.other') }}
          </button>
          <div
            v-if="localDeliveryToEventId === null"
            class="grid-form squircle-lg col-span-3 border px-4 py-2"
          >
            <div>
              <div class="col-span-2">
                <PlaceInput
                  v-model:place-model="localPlaceModel"
                  v-model:place-id="localPlaceModel.place_id"
                  v-model:place-type="localPlaceModel.place_kind"
                  :available-place-type="[
                    PlaceType.FUNERARIUM,
                    PlaceType.SALLE_FUNERARIUM,
                    PlaceType.EGLISE,
                    PlaceType.CIMETIERE,
                    PlaceType.CREMATORIUM,
                    PlaceType.WIKI_FUNERARIUM,
                    PlaceType.WIKI_SALLE_CEREMONIE,
                    PlaceType.WIKI_SALLE_DE_RECEPTION
                  ]"
                  :default-place-type="PlaceType.SALLE_FUNERARIUM"
                  default-place-group="company_place"
                  :label="$t('attributes.place')"
                  name="delivery_place"
                  edition-mode
                  @update:place-type="(v) => (currentPlaceKind = v)"
                  @update:place-id="(v) => (currentPlaceId = v)"
                  @update:place-model="onPlaceModelChange"
                />
              </div>
            </div>
            <div>
              <BaseShowEditDateTimePickerv2
                v-model:date="localDeliveryDateTime"
                :time-undefined="false"
                :label-date="$t('attributes.date')"
                edition-mode
                date=""
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import BaseShowEditDateTimePickerv2 from '@c/addf-package/components/BaseShowEditDatePicker/BaseShowEditDateTimePickerv2.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import { mapGetters } from 'vuex'
import StockSelectSupplierOrder from '@c/Stock/Component/component/StockSelectSupplierOrder.vue'
import PlaceInput from '@c/addf-package/components/Place/PlaceInput.vue'
import { PlaceType } from '@/assets/enums/index.js'

export default defineComponent({
  name: 'StockSelectNewOrder',
  components: {
    PlaceInput,
    StockSelectSupplierOrder,
    BaseButton,
    BaseShowEditDateTimePickerv2
  },
  props: {
    deliveryToEventId: {
      type: String,
      required: true
    },
    placeModel: {
      type: Object,
      default: () => ({ place_id: null, place_type: null })
    },
    placeType: {
      type: String,
      default: null
    },
    deliveryDateTime: {
      type: String,
      required: true
    },
    supplierId: {
      type: String,
      required: true
    },
    events: {
      type: Array,
      required: true
    },
    supplierWithOrders: {
      type: Array,
      required: false,
      default: () => []
    },
    localOriginal: {
      type: [Object, Array],
      required: false,
      default: null
    },
    newOrders: {
      type: [Object, Array],
      required: false,
      default: null
    }
  },
  emits: [
    'update:deliveryToEventId',
    'update:deliveryPlaceType',
    'update:deliveryPlaceId',
    'update:supplierId',
    'update:deliveryDateTime',
    'update:new-orders',
    'change-view',
    'update:placeModel',
    'choose-suplier-order'
  ],
  data() {
    return {
      localDelivery: {
        place_model: null,
        place_id: null,
        place_type: null
      },
      currentPlaceKind: null,
      currentPlaceId: null
    }
  },
  computed: {
    ...mapGetters({
      suppliers: 'metadata/getSuppliersMetadata',
      locations: 'metadata/getLocationsMetadata'
    }),
    PlaceType() {
      return PlaceType
    },
    localDeliveryToEventId: {
      get() {
        return this.deliveryToEventId
      },
      set(value) {
        this.$emit('update:deliveryToEventId', value)
      }
    },
    localPlaceModel: {
      get() {
        return this.placeModel ?? { place_id: null, place_kind: null }
      },
      set(value) {
        this.$emit('update:placeModel', value)
      }
    },
    localDeliveryDateTime: {
      get() {
        return this.deliveryDateTime
      },
      set(value) {
        this.$emit('update:deliveryDateTime', value)
      }
    },
    localSupplierId: {
      get() {
        return this.supplierId
      },
      set(value) {
        this.$emit('update:supplierId', value)
      }
    },
    filteredNewOrders() {
      return this.newOrders.filter((order) => {
        return order.recipient_id === this.localSupplierId
      })
    }
  },
  methods: {
    handleSupplierOrderClick(supplier_order) {
      if (supplier_order.recipient_id !== this.localSupplierId) {
        return
      }

      this.$emit('choose-suplier-order', supplier_order.id)
    },
    onPlaceModelChange(model) {
      this.$emit('update:placeModel', {
        ...model,
        id: this.currentPlaceId ?? model?.id,
        type: this.currentPlaceKind,
        kind: this.currentPlaceKind,
        place_id: this.currentPlaceId,
        place_kind: this.currentPlaceKind
      })
    },
    isDisabled(dateFrom) {
      return new Date(dateFrom) < new Date()
    }
  }
})
</script>
